import { FC, ReactElement, ReactNode, cloneElement, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { UserRole } from 'constants/enums/UserRolesEnum'
import { HOME_PAGE, PUBLISHED_GAMES } from 'constants/routes'

import { useAuth } from 'contexts/AuthProvider'

import HomePage from 'pages/HomePage/HomePage'
import PublishedGame from 'pages/PublishedGame'

export type Props = {
	allowAnyAuthenticatedUser?: boolean
	children: ReactNode
	guestUserAllowed?: boolean
}

const RequireAuth: FC<Props> = ({
	children,
	guestUserAllowed = true,
	allowAnyAuthenticatedUser = false,
}) => {
	const { isAuthenticated, isAuthenticating, user } = useAuth()
	const { pathname } = useLocation()
	const name = user?.firstName
	const roleId = user?.session?.idToken?.payload?.role_id
	const isGameParticipant = !roleId || roleId === UserRole.Participant
	const isPublishedGame = pathname.startsWith(`${PUBLISHED_GAMES}/`)

	const canAccess = useMemo(() => {
		// Unauthenticated users: only guestUserAllowed pages are accessible
		if (!isAuthenticated) return guestUserAllowed

		// Authenticated users:
		// Exclude game participants for allowAnyAuthenticatedUser pages
		if (allowAnyAuthenticatedUser) return !isGameParticipant
		// For users with > than participant access, all pages are accessible
		if (!isGameParticipant) return true
		// Otherwise, for game participant users, only guestUserAllowed pages are accessible
		return guestUserAllowed
	}, [
		isAuthenticated,
		isGameParticipant,
		guestUserAllowed,
		allowAnyAuthenticatedUser,
	])

	return (
		<>
			{isAuthenticating ? (
				<span data-testid="require-auth__authenticating-message">
					...isAuthenticating
				</span>
			) : canAccess ? (
				cloneElement(children as ReactElement, {
					name,
				})
			) : !isAuthenticated && isPublishedGame ? (
				<Navigate
					replace
					state={{ customGameUrl: pathname }}
					to={HOME_PAGE}
				/>
			) : isPublishedGame ? (
				<PublishedGame />
			) : (
				<HomePage />
			)}
		</>
	)
}

export default RequireAuth
